import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import firebase from 'firebase';
import { environment } from '../../environments/environment';
import { isUsable, resolveScanState, scanDocId, scanMessage, ScanState } from './file-scan.util';

/**
 * Reads the upload scan result (fileScans, written by the scanner function) so the screens can hold back files that are
 * not clean. This is the screen side of the control: the functions that hand out download links must check the same
 * record on the server.
 */
@Injectable({
  providedIn: 'root',
})
export class FileScanService {
  constructor(private snackBar: MatSnackBar) {}

  /**
   * Files uploaded from this date on need a clean scan record. Leave `config.scanEnforcedFrom` unset until the scanner
   * function is live, otherwise new uploads would wait for a scan that never comes.
   */
  private get enforcedFrom(): Date | null {
    const value = (environment.config as any).scanEnforcedFrom;
    const date = value ? new Date(value) : null;
    return date && !isNaN(date.getTime()) ? date : null;
  }

  /** State of one file (path in the Firebase Storage bucket). */
  async getState(filePath: string): Promise<ScanState> {
    if (!filePath) {
      return 'legacy';
    }
    const ref = firebase.storage().ref().child(filePath);
    let record = null;
    try {
      const id = await scanDocId(ref.bucket, filePath);
      const snap = await firebase.firestore().collection('fileScans').doc(id).get();
      record = snap.exists ? snap.data() : null;
    } catch (err) {
      // The server checks too; do not block a file only because the record could not be read here.
      console.warn('Could not read the scan record:', err);
      return 'legacy';
    }

    let created: Date | null = null;
    if (!record && this.enforcedFrom) {
      try {
        created = new Date((await ref.getMetadata()).timeCreated);
      } catch (err) {
        created = null;
      }
    }
    return resolveScanState(record, created, this.enforcedFrom);
  }

  /** Resolves with the state; when the file cannot be used it tells the user why. */
  async ensureUsable(filePath: string): Promise<{ allowed: boolean; state: ScanState; message: string }> {
    const state = await this.getState(filePath);
    const allowed = isUsable(state);
    const message = allowed ? '' : scanMessage(state);
    if (!allowed) {
      this.snackBar.open(message, 'OK', { duration: 9000, verticalPosition: 'top' });
    }
    return { allowed, state, message };
  }

  /**
   * Live states of the files of one case, by storage path (only files that have a scan record).
   * The query includes the owner so the Firestore rules can allow it (they check ownerID).
   */
  watchCase(caseName: string, ownerID: string, onChange: (states: Map<string, ScanState>) => void): () => void {
    if (!caseName) {
      return () => {};
    }
    let query: firebase.firestore.Query = firebase.firestore().collection('fileScans').where('caseName', '==', caseName);
    if (ownerID) {
      query = query.where('ownerID', '==', ownerID);
    }
    return query.onSnapshot(
        snapshot => {
          const states = new Map<string, ScanState>();
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data.objectPath) {
              states.set(data.objectPath, resolveScanState(data, null, null));
            }
          });
          onChange(states);
        },
        err => console.warn('Could not watch the scan records:', err),
      );
  }
}
